import { ApplicationConfig, LOCALE_ID, Provider } from '@angular/core';
import { provideRouter } from '@angular/router';
import { routes } from './app.routes';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideHttpClient, withInterceptors, HttpInterceptorFn } from '@angular/common/http';
import { providePrimeNG } from 'primeng/config';
import Aura from '@primeuix/themes/aura';
import { provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { environment } from './environments/environment';

// Interceptor: prefix API base URL se l'URL non è assoluto
const apiPrefixInterceptor: HttpInterceptorFn = (req, next) => {
  if (/^https?:\/\//i.test(req.url)) return next(req);
  const url = `/${req.url.replace(/^\/+/, '')}`;
  return next(req.clone({ url }));
};

// Interceptor auth (già definito nel tuo progetto)
import { authInterceptor } from './core/interceptors/auth.interceptor';

//  Cantieri: Port + Adapter + NgRx
import { CantieriPort } from './core/services/cantieri/cantieri.port';
import { CantieriHttpService } from './core/services/cantieri/cantieri.http.service';
import { cantieriReducer } from './state/cantieri/cantieri.reducer';
import { CantieriEffects } from './state/cantieri/cantieri.effects';

// Preventivi Port + Adapter + Ngrx
import { PreventiviPort } from './core/services/preventivi/preventivi.port';
import { PreventiviHttpService } from './core/services/preventivi/preventivi-http.service';
import { preventiviReducer } from './state/preventivi/preventivi.reducer';
import { PreventiviEffects } from './state/preventivi/preventivi.effects';

// Fatture Port + Adapter + NgRx
import { FatturePort } from './core/services/fatture/fatture.port';
import { FattureHttpService } from './core/services/fatture/fatture-http.service';
import { fattureReducer } from './state/fatture/fatture.reducer';
import { FattureEffects } from './state/fatture/fatture.effects';

// Attrezzature
import { AttrezzaturePort } from './core/services/attrezzature/attrezzatture.port';
import { AttrezzatureReducer } from './state/attrezzature/attrezzature.reducer';
import { AttrezzatureEffects } from './state/attrezzature/attrezzature.effects';
import { AttrezzatureHttpService } from './core/services/attrezzature/attrezzature.http.service';

// Garage
import { GaragePort } from './core/services/garage/garage.port';
import { GarageHttpService } from './core/services/garage/garage-http.service';
import { GarageReducer } from './state/garage/garage.reducer';
import { GarageEffects } from './state/garage/garage.effects';

// Anagrafiche fornitori e clienti
import { FornitoriPort } from './core/services/fornitori/fornitori.port';
import { FornitoriHttpService } from './core/services/fornitori/fornitori-http.service';
import { anagraficheReducer } from './state/anagrafiche/anagrafiche.reducer';

import { ClientiPort } from './core/services/clienti/clienti.port';
import { ClientiHttpService } from './core/services/clienti/clienti.http.service';

// Stats
import { StatsPort } from './core/services/stats/stats.port';
import { StatsHttpService } from './core/services/stats/stats.http.service';
import { statsReducer } from './state/stats/stats.reducer';
import { StatsEffects } from './state/stats/stats.effects';

// Tipologia Cantieri
import { TipoCantiereHttpService } from './core/services/tipoCantiere/tipo-cantiere.http.service';
import { TipoCantierePort } from './core/services/tipoCantiere/tipo-cantiere.port';
import { AnagraficheEffects } from './state/anagrafiche/anagrafiche.effects';
import { tipologieCantieriReducer } from './state/tipologieCantieri/tipologie-cantieri.reducer';
import { TipologieCantieriEffects } from './state/tipologieCantieri/tipologie-cantieri.effects';
import { tipologieMezziReducer } from './state/tipologieMezzi/tipologie-mezzi.reducer';
import { TipologieMezziEffects } from './state/tipologieMezzi/tipologie-mezzi.effects';

// Capitoli
import { CapitoliPort } from './core/services/capitoli/capitoli.port';
import { CapitoliHttpService } from './core/services/capitoli/capitoli.http.service';
import { capitoliReducer } from './state/capitoli/capitoli.reducer';
import { CapitoliEffects } from './state/capitoli/capitoli.effects';

// Voci capitoli
import { VociPort } from './core/services/voci/voci.port';
import { VociHttpService } from './core/services/voci/voci.http.service';
import { vociReducer } from './state/voci/voci.reducer';
import { VociEffects } from './state/voci/voci.effects';

// Dipendenti
import { DipendentiPort } from './core/services/dipendenti/dipendenti.port';
import { DipendentiHttpService } from './core/services/dipendenti/dipendenti-http.service';
import { dipendentiReducer } from './state/dipendenti/dipendenti.reducer';
import { DipendentiEffects } from './state/dipendenti/dipendenti.effects';

// Helper: gestisce sia boolean che oggetto { feature: boolean }
const useMock = (flag: unknown, key: 'fatture' | 'attrezzature') =>
  typeof flag === 'boolean' ? flag : !!(flag as any)?.[key];

const cantieriProvider: Provider = {
  provide: CantieriPort,
  useClass: CantieriHttpService,
};

const preventiviProvider: Provider = {
  provide: PreventiviPort,
  useClass: PreventiviHttpService,
};

const capitoliProvider: Provider = {
  provide: CapitoliPort,
  useClass: CapitoliHttpService,
};

const vociProvider: Provider = {
  provide: VociPort,
  useClass: VociHttpService,
};

const fattureProvider: Provider = {
  provide: FatturePort,
  useClass: FattureHttpService,
};

const attrezzatureProvider: Provider = {
  provide: AttrezzaturePort,
  useClass: AttrezzatureHttpService,
};

const garageProvider: Provider = {
  provide: GaragePort,
  useClass: GarageHttpService,
};

const fornitoriProvider: Provider = {
  provide: FornitoriPort,
  useClass: FornitoriHttpService,
};

const clientiProvider: Provider = {
  provide: ClientiPort,
  useClass: ClientiHttpService,
};

const tipoCantiereProvider: Provider = {
  provide: TipoCantierePort,
  useClass: TipoCantiereHttpService,
};

const statsProvider: Provider = {
  provide: StatsPort,
  useClass: StatsHttpService,
};

const dipendentiProvider: Provider = {
  provide: DipendentiPort,
  useClass: DipendentiHttpService,
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes),
    provideAnimations(),
    providePrimeNG({
      theme: {
        preset: Aura,
        options: {
          darkModeSelector: '.dark-mode',
        },
      },
    }),
    provideHttpClient(withInterceptors([authInterceptor, apiPrefixInterceptor])),

    { provide: LOCALE_ID, useValue: 'it-IT' },

    // Store NgRx
    provideStore({
      cantieri: cantieriReducer,
      preventivi: preventiviReducer,
      fatture: fattureReducer,
      attrezzature: AttrezzatureReducer,
      garage: GarageReducer,
      anagrafiche: anagraficheReducer,
      capitoli: capitoliReducer,
      voci: vociReducer,
      stats: statsReducer,
      tipologieCantieri: tipologieCantieriReducer,
      tipologieMezzi: tipologieMezziReducer,
      dipendenti: dipendentiReducer,
    }),
    provideEffects([
      CantieriEffects,
      PreventiviEffects,
      FattureEffects,
      AttrezzatureEffects,
      GarageEffects,
      AnagraficheEffects,
      CapitoliEffects,
      VociEffects,
      StatsEffects,
      TipologieCantieriEffects,
      TipologieMezziEffects,
      DipendentiEffects,
    ]),
    provideStoreDevtools({ maxAge: 25, logOnly: environment.production }),

    // Providers Port → Adapter
    cantieriProvider,
    preventiviProvider,
    fattureProvider,
    attrezzatureProvider,
    fornitoriProvider,
    clientiProvider,
    tipoCantiereProvider,
    garageProvider,
    capitoliProvider,
    vociProvider,
    statsProvider,
    dipendentiProvider,
  ],
};
